// Map themes and how each topic looks. A map's look is CONTENT (it is saved in
// the file and exported exactly as shown), so it never follows the app's light
// or dark mode: a "dark" map is its own theme.

export const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** Branch colours, in the order first-level topics take them. */
export const PALETTE = [
  ['blue', '#2563eb'], ['violet', '#7c3aed'], ['pink', '#db2777'], ['orange', '#ea580c'], ['green', '#16a34a'],
  ['teal', '#0d9488'], ['amber', '#d97706'], ['red', '#dc2626'], ['indigo', '#4f46e5'], ['cyan', '#0891b2'],
];
/** Every colour a topic can be given (the palette, plus neutrals). */
export const COLORS = [...PALETTE, ['slate', '#475569'], ['black', '#18181b']];
export const colorHex = (c) => (c && c.startsWith('#') ? c : (COLORS.find(([k]) => k === c) || [])[1] || null);

export const THEMES = {
  colorful: { label: 'Colorful', bg: '#ffffff', ink: '#18181b', muted: '#71717a', root: '#18181b', rootInk: '#ffffff', l1: 'solid', l2: 'soft', shape2: 'rounded', mono: false, rel: '#64748b' },
  soft: { label: 'Soft', bg: '#fafafa', ink: '#18181b', muted: '#71717a', root: '#27272a', rootInk: '#ffffff', l1: 'soft', l2: 'soft', shape2: 'rounded', mono: false, rel: '#64748b' },
  paper: { label: 'Paper', bg: '#fbf8f1', ink: '#292524', muted: '#78716c', root: '#292524', rootInk: '#fbf8f1', l1: 'outline', l2: 'line', shape2: 'underline', mono: false, rel: '#78716c' },
  mono: { label: 'Mono', bg: '#ffffff', ink: '#18181b', muted: '#71717a', root: '#18181b', rootInk: '#ffffff', l1: 'outline', l2: 'line', shape2: 'underline', mono: true, line: '#a1a1aa', rel: '#71717a' },
  dark: { label: 'Dark', bg: '#18181b', ink: '#fafafa', muted: '#a1a1aa', root: '#fafafa', rootInk: '#18181b', l1: 'solid', l2: 'soft', shape2: 'rounded', mono: false, rel: '#a1a1aa' },
};
export const themeOf = (map) => THEMES[map?.theme] || THEMES.colorful;

// ---- colour maths ------------------------------------------------------------------
const hexRgb = (h) => { const s = h.replace('#', ''); const v = s.length === 3 ? s.split('').map((c) => c + c).join('') : s; return [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16)); };
const rgbHex = (r) => `#${r.map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('')}`;
/** `a` mixed toward `b` by t (0 = a, 1 = b). */
export const mix = (a, b, t) => { const x = hexRgb(a); const y = hexRgb(b); return rgbHex(x.map((v, i) => v + (y[i] - v) * t)); };
const lum = (h) => { const [r, g, b] = hexRgb(h).map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; }); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };
/** Readable text on a fill: white on dark colours, near-black on light ones. */
export const inkOn = (fill) => (lum(fill) > 0.42 ? '#18181b' : '#ffffff');

const SIZE_K = { s: 0.85, m: 1, l: 1.25, xl: 1.6 };

/**
 * The look of one topic. `color` is its branch colour (already resolved),
 * `depth` 0 for the central topic and floating topics' tops.
 */
export function topicStyle(nd, depth, color, T, isFloatingTop = false, bubble = null) {
  const lvl = depth === 0 && !isFloatingTop ? 0 : depth <= 1 ? 1 : 2;
  const k = SIZE_K[nd.size] || 1;
  const fontSize = Math.round((lvl === 0 ? 22 : lvl === 1 ? 16 : 14) * k);
  const weight = nd.bold ? 700 : lvl === 0 ? 700 : lvl === 1 ? 600 : 400;
  let shape = nd.shape || (lvl === 2 ? T.shape2 : 'rounded');
  let mode = nd.fill || (lvl === 0 ? 'root' : lvl === 1 ? T.l1 : T.l2);
  if (bubble) {
    // a bubble map: every topic is a circle (dots: a solid dot with its label under it)
    shape = 'circle';
    if (bubble === 'dots') mode = lvl === 0 && !nd.color ? 'root' : 'solid';
    else if (mode === 'line') mode = lvl === 1 ? 'solid' : 'outline';
  } else if (shape === 'underline' || shape === 'none') mode = 'line';
  else if (mode === 'line') mode = 'soft'; // a box shape picked on a theme whose topics are lines
  const c = T.mono && !nd.color ? T.ink : color;
  let fill = 'none'; let stroke = 'none'; let strokeW = 0; let ink = T.ink;
  if (mode === 'root') {
    fill = nd.color ? colorHex(nd.color) : T.root; ink = nd.color ? inkOn(fill) : T.rootInk;
  } else if (mode === 'solid') {
    fill = c; ink = inkOn(c);
  } else if (mode === 'soft') {
    fill = mix(c, T.bg, 0.86); stroke = mix(c, T.bg, 0.45); strokeW = 1;
  } else if (mode === 'outline') {
    fill = T.bg; stroke = c; strokeW = 1.5;
  }
  const line = T.mono && !nd.color ? (T.line || T.muted) : color;
  let padY = lvl === 0 ? 14 : lvl === 1 ? 9 : 6;
  let padX = lvl === 0 ? 24 : lvl === 1 ? 16 : 11;
  if (mode === 'line') { padY = 5; padX = 4; }
  padX = Math.round(padX * k); padY = Math.round(padY * k);
  const radius = shape === 'rect' ? 3 : shape === 'rounded' ? (lvl === 0 ? 12 : 8) : 0;
  return {
    lvl, shape, mode, fill, stroke, strokeW, ink, fontSize, weight, italic: !!nd.italic,
    padX, padY, radius, line, maxW: Math.round((bubble ? (lvl === 0 ? 170 : lvl === 1 ? 140 : 120) : (lvl === 0 ? 300 : lvl === 1 ? 260 : 240)) * Math.max(1, k * 0.9)),
    font: `${nd.italic ? 'italic ' : ''}${weight} ${fontSize}px ${FONT}`,
    lineH: Math.round(fontSize * 1.32),
  };
}
